import {
  Box,
  Flex,
  Divider,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalOverlay,
  Table,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Input,
  Checkbox,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  TapeBuilderUI,
  OperationNodes,
} from '@finley-technologies/tape-builder';
import type { TapeBuilderNodeAbstract } from '@finley-technologies/tape-builder';

import { ConfigureParams } from './types';
import { colors } from '~/packages/common-frontend/colors';
import {
  AddIconButton,
  MinusIconButton,
} from '~/packages/common-frontend/components/IconButton';
import { Typography } from '~/packages/common-frontend/components/Typography';

interface Xlsx2Csv2ExtractModalProps {
  nodeConfiguration: OperationNodes.Xlsx2Csv2.UIConfig;
  onAutoConfigure: (configuration: ConfigureParams) => void;
  modalControls: ReturnType<typeof useDisclosure>;
}

const ExtractValuesConfigurationModal = ({
  nodeConfiguration,
  onAutoConfigure,
  modalControls,
}: Xlsx2Csv2ExtractModalProps) => {
  finleyAssert(
    nodeConfiguration.mode === 'extractValues',
    `Expected "extractValues" mode for configuration modal [mode:${nodeConfiguration.mode}]`,
  );
  const { fields } = nodeConfiguration;
  // Note: this modal is only for "extractValues" mode when all values are "cell" and not "cellRange"
  // we need to revisit this to support cellRange.
  finleyAssert(
    fields.every((x) => x.cell && !x.cellRange),
    'Expected all fields to have a cell and not a cellRange',
  );

  const toast = useToast();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [newCellMap, setNewCellMap] = useState<Map<string, string>>(
    new Map(
      fields.map(({ field, cell }) => {
        finleyAssert(cell, 'Expected cell to be defined');
        return [field, cell];
      }),
    ),
  );

  const updateSelected = useCallback((field: string): void => {
    setSelected((prev) => {
      const updatedSet = new Set(prev);
      if (updatedSet.has(field)) {
        updatedSet.delete(field);
      } else {
        updatedSet.add(field);
      }
      return updatedSet;
    });
  }, []);

  const shiftCells = useCallback(
    (params: { type: 'row' | 'column'; amount: number }): void => {
      const { type, amount } = params;

      if (selected.size <= 0) return;
      if (amount === 0) return;

      setNewCellMap((curr) => {
        const updatedMap = new Map(curr);
        for (const field of selected) {
          const latestCell = updatedMap.get(field) ?? 'A1';
          if (!commonUtils.isProperExcelCoords(latestCell)) {
            continue;
          }

          const coords = commonUtils.excelCoordsToNumberCoords(latestCell);
          const newNumCoords = {
            r: Math.max(coords.r + (type === 'row' ? amount : 0), 0),
            c: Math.max(coords.c + (type === 'column' ? amount : 0), 0),
          };
          const newCell = commonUtils.numberCoordsToExcelCoords(newNumCoords);
          updatedMap.set(field, newCell);
        }
        return updatedMap;
      });
    },
    [selected],
  );

  const updateOnClick = useCallback(() => {
    const errors: string[] = [];
    const seenCellAndSheet = new Set<string>();

    let didAnyChange = false;
    const updatedFields = fields.map((config) => {
      const newCell = newCellMap.get(config.field) ?? config.cell;
      finleyAssert(newCell, 'Expected new cell to be defined');

      if (newCell !== config.cell) {
        didAnyChange = true;
      }

      if (!commonUtils.isProperExcelCoords(newCell)) {
        errors.push(
          `Improper cell coordinate [field:${config.field}] [cell:${newCell}]`,
        );
      }

      if (seenCellAndSheet.has(`${config.sheetName}-${newCell}`)) {
        errors.push(
          `Duplicate cell [field:${config.field}] [sheetName:${config.sheetName}] [cell:${newCell}]`,
        );
      } else {
        seenCellAndSheet.add(`${config.sheetName}-${newCell}`);
      }

      return { ...config, cell: newCell };
    });

    if (errors.length > 0) {
      onAutoConfigure({ isValid: false, operation: 'xlsx2csv2', errors });
      return;
    }

    if (!didAnyChange) {
      toast({
        status: 'info',
        duration: 5000,
        position: 'top',
        isClosable: true,
        title: 'No cell placements are updated',
      });
      return;
    }

    onAutoConfigure({
      isValid: true,
      operation: 'xlsx2csv2',
      configuration: { ...nodeConfiguration, fields: updatedFields },
    });
    modalControls.onClose();
  }, [
    fields,
    modalControls,
    newCellMap,
    nodeConfiguration,
    onAutoConfigure,
    toast,
  ]);

  return (
    <Modal
      isOpen={modalControls.isOpen}
      onClose={modalControls.onClose}
      size="5xl"
    >
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader pb="8px">
          Xlsx2Csv2: Currently Adjusting {selected.size} Cells
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex mb="8px" alignItems="center">
            <Button
              type="button"
              variant="primary"
              px="8px"
              h="24px"
              fontWeight="semibold"
              mr="0.5rem"
              onClick={() => setSelected(new Set(fields.map((x) => x.field)))}
            >
              Select All
            </Button>
            <Button
              type="button"
              variant="primary"
              px="8px"
              h="24px"
              fontWeight="semibold"
              onClick={() => setSelected(new Set())}
              isDisabled={selected.size === 0}
            >
              De-Select All
            </Button>

            <Divider
              orientation="vertical"
              mx="1rem"
              h="16px"
              bgColor="white"
            />

            <Typography TextProps={{ mr: '0.5rem' }}>Rows:</Typography>
            <AddIconButton
              onClick={() => shiftCells({ type: 'row', amount: 1 })}
              isDisabled={selected.size === 0}
              h="16px"
              w="16px"
              minW="0"
              mr="0.5rem"
            />
            <MinusIconButton
              onClick={() => shiftCells({ type: 'row', amount: -1 })}
              isDisabled={selected.size === 0}
              h="16px"
              w="16px"
              minW="0"
            />

            <Divider
              orientation="vertical"
              mx="1rem"
              h="16px"
              bgColor="white"
            />

            <Typography TextProps={{ mr: '0.5rem' }}>Columns:</Typography>
            <AddIconButton
              onClick={() => shiftCells({ type: 'column', amount: 1 })}
              isDisabled={selected.size === 0}
              h="16px"
              w="16px"
              minW="0"
              mr="0.5rem"
            />
            <MinusIconButton
              onClick={() => shiftCells({ type: 'column', amount: -1 })}
              isDisabled={selected.size === 0}
              h="16px"
              w="16px"
              minW="0"
            />
          </Flex>

          <Box overflow="auto" maxH="600px">
            <Table layout="fixed">
              <Thead position="sticky" top={0} zIndex="docked">
                <Tr>
                  <Th p="0" w="16px" />
                  <Th>Field</Th>
                  <Th w="150px">Sheet</Th>
                  <Th w="120px" textAlign="center">
                    Current Cell
                  </Th>
                  <Th w="120px" textAlign="center">
                    New Cell
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {fields.map((fieldConfig) => {
                  const { field, cell, sheetName } = fieldConfig;
                  const newCell = newCellMap.get(field) ?? cell;
                  finleyAssert(newCell, 'Expected newCell to be defined');

                  return (
                    <Tr key={field}>
                      <Td px="8px">
                        <Checkbox
                          type="checkbox"
                          isChecked={selected.has(field)}
                          onChange={() => updateSelected(field)}
                        />
                      </Td>
                      <Td>
                        <Typography
                          TextProps={{
                            w: 'fit-content',
                            maxW: 'full',
                            onClick: () => updateSelected(field),
                            _hover: { cursor: 'pointer' },
                          }}
                        >
                          {field}
                        </Typography>
                      </Td>
                      <Td whiteSpace="nowrap">{sheetName}</Td>
                      <Td textAlign="center" px="8px">
                        {cell}
                      </Td>
                      <Td py="0" px="8px">
                        <Input
                          value={newCell}
                          onChange={(evt) => {
                            const cellInput = evt.target.value.toUpperCase();
                            setNewCellMap((curr) => {
                              const updatedMap = new Map(curr);
                              updatedMap.set(field, cellInput);
                              return updatedMap;
                            });
                          }}
                          textAlign="center"
                          h="36px"
                          px="0"
                          // eslint-disable-next-line no-nested-ternary
                          {...(!commonUtils.isProperExcelCoords(newCell)
                            ? {
                                borderWidth: 'medium',
                                borderColor: colors.red[400],
                              }
                            : newCellMap.get(field) !== cell
                            ? {
                                borderWidth: 'medium',
                                borderColor: colors['v2-aurora'][500],
                              }
                            : {})}
                        />
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </ModalBody>

        <ModalFooter gap="0.75rem">
          <Button variant="secondary" onClick={modalControls.onClose}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={updateOnClick}>
            Update
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

interface Xlsx2Csv2ConfigurationPanelProps {
  tapeBuilderNode: TapeBuilderNodeAbstract;
  onAutoConfigure: (configuration: ConfigureParams) => void;
}
export const Xlsx2Csv2ConfigurationPanel = ({
  tapeBuilderNode,
  onAutoConfigure,
}: Xlsx2Csv2ConfigurationPanelProps) => {
  const extractValueModal = useDisclosure();

  const { configuration } = tapeBuilderNode;

  if (
    !TapeBuilderUI.operationNodes.operation_xlsx2Csv2.isConfig(configuration)
  ) {
    return (
      <Tooltip label="Invalid configuration schema">
        <Button type="button" isDisabled>
          Auto-configure cells
        </Button>
      </Tooltip>
    );
  }

  const { mode } = configuration;

  return (
    <Box id="xlsx2csv2-configuration-panel">
      <Tooltip
        isDisabled={mode === 'extractValues'}
        label='Only able to configure "extractValues" mode'
      >
        <Button
          type="button"
          // Currently only support supplement for "extractValues"
          isDisabled={mode !== 'extractValues'}
          onClick={extractValueModal.onOpen}
        >
          Auto-configure cells
        </Button>
      </Tooltip>

      {extractValueModal.isOpen && (
        <ExtractValuesConfigurationModal
          nodeConfiguration={configuration}
          onAutoConfigure={onAutoConfigure}
          modalControls={extractValueModal}
        />
      )}
    </Box>
  );
};
